/* Hot Desking — manage PINs on extensions + view active sessions */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { DeviceMobile, Key, Power, FloppyDisk } from '@phosphor-icons/react';

const HotDesking = () => {
    const [extensions, setExtensions] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [pinFor, setPinFor] = useState(null);
    const [pinValue, setPinValue] = useState('');

    const load = async () => {
        const [e, s] = await Promise.all([
            api.get('/extensions'),
            api.get('/hot-desk/sessions'),
        ]);
        setExtensions(e.data);
        setSessions(s.data);
    };
    useEffect(() => { load(); }, []);

    const savePin = async () => {
        if (pinValue.length < 4) return toast.error('PIN min. 4 znaky');
        try {
            await api.post(`/hot-desk/${pinFor.id}/set-pin`, { pin: pinValue });
            toast.success(`Hot Desk povolen pro klapku ${pinFor.extension}`);
            setPinFor(null); setPinValue('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Chyba');
        }
    };

    const disable = async (ext) => {
        if (!confirm(`Opravdu vypnout hot desk pro klapku ${ext.extension}?`)) return;
        try {
            await api.post(`/hot-desk/${ext.id}/disable`);
            toast.success('Hot Desk vypnut');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Chyba');
        }
    };

    const hdEnabled = (e) => e.hot_desk_enabled || sessions.find((s) => s.id === e.id);

    return (
        <div data-testid="hot-desk-page">
            <div className="mb-6">
                <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                    <DeviceMobile size={28} style={{ color: 'var(--brand)' }} />
                    Hot Desking
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Přihlášení na libovolný IP telefon pomocí klapky + PIN (žádná MAC adresa)
                </p>
            </div>

            <div className="rounded-xl border overflow-hidden mb-4"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <div className="px-5 py-3 border-b text-xs font-bold uppercase tracking-[0.15em]"
                     style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>
                    Aktivní sezení ({sessions.length})
                </div>
                <table className="w-full text-sm" data-testid="hd-sessions">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider"
                            style={{ color: 'var(--text-disabled)' }}>
                            <th className="px-5 py-2">Klapka</th>
                            <th className="px-5 py-2">Jméno</th>
                            <th className="px-5 py-2">Device ID</th>
                            <th className="px-5 py-2">Štítek</th>
                            <th className="px-5 py-2">Přihlášeno</th>
                        </tr>
                    </thead>
                    <tbody>
                    {sessions.length === 0 ? (
                        <tr><td colSpan={5} className="px-5 py-8 text-center text-sm"
                                style={{ color: 'var(--text-disabled)' }}>Žádné aktivní hot-desk přihlášení</td></tr>
                    ) : sessions.map((s) => (
                        <tr key={s.id} className="border-t"
                            style={{ borderColor: 'var(--border-default)' }}>
                            <td className="px-5 py-3 font-mono font-bold">{s.extension}</td>
                            <td className="px-5 py-3">{s.display_name}</td>
                            <td className="px-5 py-3 font-mono text-xs">{s.current_device_id}</td>
                            <td className="px-5 py-3 text-xs">{s.current_device_label || '—'}</td>
                            <td className="px-5 py-3 font-mono text-xs">
                                {s.hot_desk_logged_in_at && new Date(s.hot_desk_logged_in_at).toLocaleString('cs-CZ')}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <div className="rounded-xl border overflow-hidden"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <div className="px-5 py-3 border-b text-xs font-bold uppercase tracking-[0.15em]"
                     style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>
                    Klapky ({extensions.length})
                </div>
                <table className="w-full text-sm" data-testid="hd-extensions">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider"
                            style={{ color: 'var(--text-disabled)' }}>
                            <th className="px-5 py-2">Klapka</th>
                            <th className="px-5 py-2">Jméno</th>
                            <th className="px-5 py-2">Email</th>
                            <th className="px-5 py-2">Hot Desk</th>
                            <th className="px-5 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                    {extensions.map((e) => (
                        <tr key={e.id} className="border-t"
                            style={{ borderColor: 'var(--border-default)' }}>
                            <td className="px-5 py-3 font-mono font-bold">{e.extension}</td>
                            <td className="px-5 py-3">{e.display_name}</td>
                            <td className="px-5 py-3 text-xs font-mono">
                                {e.assigned_user_email || '—'}
                            </td>
                            <td className="px-5 py-3">
                                {hdEnabled(e) ? (
                                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded"
                                          style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
                                        Povoleno
                                    </span>
                                ) : (
                                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
                                          style={{ color: 'var(--text-disabled)' }}>
                                        —
                                    </span>
                                )}
                            </td>
                            <td className="px-5 py-3 text-right">
                                <button
                                    data-testid={`hd-pin-${e.extension}`}
                                    onClick={() => { setPinFor(e); setPinValue(''); }}
                                    className="px-3 py-1 rounded text-xs border mr-1 hover:bg-[var(--bg-surface-hover)]"
                                    style={{ borderColor: 'var(--border-default)' }}
                                >
                                    <Key size={12} className="inline mr-1" /> Nastavit PIN
                                </button>
                                {hdEnabled(e) && (
                                    <button
                                        data-testid={`hd-disable-${e.extension}`}
                                        onClick={() => disable(e)}
                                        className="px-3 py-1 rounded text-xs border hover:border-[var(--danger)] hover:text-[var(--danger)]"
                                        style={{ borderColor: 'var(--border-default)' }}>
                                        <Power size={12} className="inline mr-1" /> Vypnout
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {pinFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                     style={{ background: 'rgba(0,0,0,0.6)' }}
                     onClick={() => setPinFor(null)}>
                    <div className="rounded-xl border p-6 max-w-md w-full"
                         data-testid="hd-pin-modal"
                         onClick={(e) => e.stopPropagation()}
                         style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                        <h3 className="text-lg font-bold mb-1">Hot Desk PIN — klapka {pinFor.extension}</h3>
                        <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
                            Uživatel si po zadání tohoto PINu může přihlásit svou klapku na jakémkoli sdíleném telefonu.
                        </p>
                        <input
                            data-testid="hd-pin-input"
                            type="password"
                            value={pinValue}
                            onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 12))}
                            placeholder="4-12 číslic"
                            className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-3 font-mono text-2xl text-center tracking-[0.3em] mb-4"
                            style={{ borderColor: 'var(--border-default)' }}
                            autoFocus
                        />
                        <div className="flex gap-2">
                            <button data-testid="hd-pin-save" onClick={savePin}
                                    className="flex-1 px-4 py-2 rounded-lg font-bold text-sm"
                                    style={{ background: 'var(--brand)', color: '#000' }}>
                                <FloppyDisk size={14} className="inline mr-2" /> Uložit
                            </button>
                            <button onClick={() => setPinFor(null)}
                                    className="px-4 py-2 rounded-lg border text-sm"
                                    style={{ borderColor: 'var(--border-default)' }}>
                                Zrušit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HotDesking;
